import { createContext, useContext } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useSession } from '@/services/session';
import { useBedtime } from '@/app/useBedtime';
import type { ChildProfile } from '@/contracts/family';
import { SparkleGlyph } from '@/design-system/Sparkle';

const ChildContext = createContext<ChildProfile | null>(null);

/**
 * Resolves the route's `:childId` against the CURRENT account.
 *
 * The parameter is a navigation reference, never authorization (spec §3), so
 * an id that does not belong to this account resolves to nothing and the
 * visitor is returned to the profile chooser. When a backend exists, the same
 * check is repeated server-side on every data operation.
 */
export function ChildGate({ children }: { children: React.ReactNode }) {
  const { childId } = useParams();
  const loaded = useSession((s) => s.loaded);
  const child = useSession((s) => s.requireChild(childId));

  if (!loaded) {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <SparkleGlyph className="h-10 w-10 animate-twinkle text-magic" />
      </div>
    );
  }
  if (!child) return <Navigate to="/app" replace />;
  return (
    <ChildContext.Provider value={child}>
      <BedtimeSkin bedtime={child.bedtime}>{children}</BedtimeSkin>
    </ChildContext.Provider>
  );
}

/** Applies the calm palette while this child is the one reading. */
function BedtimeSkin({ bedtime, children }: { bedtime: boolean; children: React.ReactNode }) {
  useBedtime(bedtime);
  return <>{children}</>;
}

export function useChild(): ChildProfile {
  const child = useContext(ChildContext);
  if (!child) throw new Error('useChild used outside ChildGate');
  return child;
}
